import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Comments from './Comments';
import { cn } from './model/derive';

/** 태스크 전체 페이지 Activity — 요청부터 지금까지의 기록 · 필터(All · Handoffs · Comments) · 댓글 쓰기 */

const FILTERS = [
  ['all', 'All'],
  ['handoffs', 'Handoffs'],
  ['comments', 'Comments'],
];
const LIM = 5;
const DOT = {
  approved: 'bg-functional-success03',
  rejected: 'bg-functional-failed03',
  returned: 'bg-functional-warning03',
  declined: 'bg-functional-warning03',
  submitted: 'bg-green-deep02',
};
const ring = 'shadow-[0_0_0_3px_var(--bg-twodays)]';

let keptFilter = 'all';

const TaskActivity = ({ task, events, focusId, reworkId = null, onGuideDismiss = null }) => {
  const { hash } = useLocation();
  const toComment = !!focusId || !!reworkId || hash.startsWith('#comment-');
  const [filter, setFilterState] = useState(() => (toComment ? 'comments' : keptFilter));
  const [more, setMore] = useState(false);
  const setFilter = (k) => {
    keptFilter = k;
    setFilterState(k);
    setMore(false);
  };
  useEffect(() => {
    if (toComment) setFilterState('comments');
  }, [toComment]);
  useEffect(() => setMore(false), [task.id]);

  const list = filter === 'handoffs' ? events.filter((e) => e.key) : events;
  const shown = more ? list : list.slice(0, LIM);

  return (
    <section className='flex flex-col gap-3'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex flex-col gap-0.5'>
          <h2 className='text-[16px] font-medium leading-[150%] text-text-primary'>Activity</h2>
          <span className='text-[13px] leading-[140%] text-text-secondary'>
            From request to now · newest first
          </span>
        </div>
        <div role='tablist' className='flex gap-0.5 rounded-lg bg-green-deep03/[0.08] p-[3px]'>
          {FILTERS.map(([k, label]) => (
            <button
              key={k}
              type='button'
              role='tab'
              aria-selected={filter === k}
              onClick={() => setFilter(k)}
              className={cn(
                'rounded-md px-3 py-1.5 text-[13px] font-medium leading-[140%]',
                filter === k
                  ? 'bg-grayscale-gray00 text-text-primary'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {filter !== 'comments' && (
        <ol aria-label='Activity log' className='flex flex-col py-1'>
          {shown.map((e) => (
            <li key={e.n} className='grid grid-cols-[16px_minmax(0,1fr)] gap-x-3'>
              <span className='relative flex justify-center'>
                <span aria-hidden className='absolute inset-y-0 w-[1.5px] bg-green-deep03/[0.12]' />
                {e.key ? (
                  <span
                    aria-hidden
                    className={cn('relative mt-3 h-2.5 w-2.5 rounded-full', ring, DOT[e.k] ?? 'bg-green-deep04')}
                  />
                ) : (
                  <span
                    aria-hidden
                    className={cn('relative mt-2.5 h-1.5 w-1.5 rounded-full bg-grayscale-gray04', ring)}
                  />
                )}
              </span>
              {e.key ? (
                <div className='mb-2 mt-1 flex min-w-0 flex-col gap-0.5 rounded-xl border border-green-deep03/[0.12] bg-grayscale-gray00 px-3 py-2'>
                  <span className='flex flex-wrap items-baseline gap-2'>
                    <span className='text-[14px] font-medium leading-[140%] text-text-primary'>
                      {e.title}
                    </span>
                    <span className='text-[12px] leading-[140%] text-text-secondary'>
                      {e.who}
                      {e.time ? ` · ${e.time}` : ''}
                    </span>
                  </span>
                  {e.detail && (
                    <span className='text-[13px] leading-[150%] text-text-secondary [text-wrap:pretty]'>
                      {e.detail}
                    </span>
                  )}
                </div>
              ) : (
                <div className='flex min-w-0 flex-wrap items-baseline gap-1.5 pb-2 pt-1 text-[13px] leading-[150%]'>
                  <span className='text-text-primary'>{e.who}</span>
                  <span className='text-text-secondary'>{e.title}</span>
                  {e.pmOnly && (
                    <span
                      title='Only the PM and the person who changed it see this'
                      className='inline-flex h-5 items-center rounded-md bg-grayscale-gray01 px-[7px] text-[11px] font-medium leading-none text-text-tertiary shadow-[inset_0_0_0_1px_theme(colors.grayscale.gray03)]'
                    >
                      Visible to PM
                    </span>
                  )}
                  {e.time && <span className='text-text-tertiary'>· {e.time}</span>}
                  {e.detail && (
                    <span className='basis-full truncate text-text-tertiary'>{e.detail}</span>
                  )}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
      {filter !== 'comments' && list.length > LIM && (
        <div className='flex pl-7'>
          {more ? (
            <button
              type='button'
              onClick={() => setMore(false)}
              className='h-8 rounded-full px-3 text-[13px] font-medium text-text-secondary hover:text-text-primary'
            >
              Show less
            </button>
          ) : (
            <button
              type='button'
              onClick={() => setMore(true)}
              className='h-8 rounded-full bg-green-deep03/[0.08] px-3 text-[13px] font-medium text-text-primary hover:bg-green-deep03/[0.14]'
            >
              Show {list.length - LIM} older
            </button>
          )}
        </div>
      )}
      <Comments
        task={task}
        focusId={focusId}
        reworkId={reworkId}
        onGuideDismiss={onGuideDismiss}
        embedded
        showThreads={filter === 'comments'}
      />
    </section>
  );
};

export default TaskActivity;
